import { getApiBaseUrl } from "@/lib/config";

export type DashboardAudience = "admin" | "user";

export class ApiError extends Error {
  status: number;
  code: string | undefined;
  body: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

function tokenKey(audience: DashboardAudience) {
  return audience === "admin" ? "anga_admin_token" : "anga_user_token";
}

function refreshKey(audience: DashboardAudience) {
  return audience === "admin" ? "anga_admin_refresh" : "anga_user_refresh";
}

export function getToken(audience: DashboardAudience) {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(tokenKey(audience));
}

export function setToken(audience: DashboardAudience, token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(tokenKey(audience), token);
  else localStorage.removeItem(tokenKey(audience));
}

export function getRefreshToken(audience: DashboardAudience) {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(refreshKey(audience));
}

export function setRefreshToken(audience: DashboardAudience, token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(refreshKey(audience), token);
  else localStorage.removeItem(refreshKey(audience));
}

export function clearAuth(audience: DashboardAudience) {
  setToken(audience, null);
  setRefreshToken(audience, null);
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { audience?: DashboardAudience } = {},
): Promise<T> {
  const { audience, headers, ...rest } = options;
  const token = audience ? getToken(audience) : null;

  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      "X-Anga-Client": "web",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });

  if (audience) {
    const renewed = response.headers.get("X-Access-Token");
    if (renewed) setToken(audience, renewed);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(
      (data as { error?: string }).error || "Request failed",
      response.status,
      (data as { code?: string }).code,
      data,
    );
  }
  return data as T;
}
